/**
 * SideNavIcon - bespoke inline-SVG glyphs for the sidebar navigation.
 *
 * Replaces the platform's generic Icons component (home / direction /
 * bookmarks / podium / performance / user) which were utility-grade
 * stock icons - the same legacy set the journey step nodes used
 * before we built StepGlyph. Six bespoke 24x24 stroke-based glyphs
 * tuned to read at 18px inside the sidebar items.
 *
 * Amir 2026-05-20: "Path B - full pass" on sidebar cleanup. Bespoke
 * icons land alongside pear active state, lowercase typography lock,
 * chrome-group separation, and density bump.
 *
 * Each glyph:
 *  - 24x24 viewBox, stroke-based (no fills except for accent dots)
 *  - strokeWidth 1.75, rounded caps/joins for a consistent geometric
 *    voice that matches the StepGlyph language on the journey row
 *  - Accepts a `color` prop applied as stroke; subtle inner accent
 *    dots use the color as fill for emphasis
 *
 * Glyph semantics:
 *  - dashboard: target/crosshair (you are here, this is the center)
 *  - journey: map path between two pins (the route you walk)
 *  - bookmarks: stacked bookmarks (saved items, two-tier visual)
 *  - leaderboard: 3 podium bars (1st/2nd/3rd ranking)
 *  - 3cq: triangle of 3 connected nodes (the 3 dimensions: critical
 *    thinking + communication + collaboration)
 *  - profile: person silhouette (the user)
 */
const SideNavIcon = ({ type, color = "currentColor" }) => {
  const baseProps = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.75,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    "data-testid": `sidenav-icon-${type}`,
  };

  switch (type) {
    case "dashboard":
      // Target / crosshair: outer ring, four-way notches, filled
      // center dot. Reads as "you are here, this is the home base."
      return (
        <svg {...baseProps}>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M12 3.5v2.5" />
          <path d="M12 18v2.5" />
          <path d="M3.5 12h2.5" />
          <path d="M18 12h2.5" />
          <circle cx="12" cy="12" r="1.8" fill={color} stroke="none" />
        </svg>
      );

    case "journey":
      // Map path: curved route with start pin + end flag. Mirrors
      // the journey trail metaphor from the dashboard breadcrumbs.
      return (
        <svg {...baseProps}>
          <path d="M5 18 C 8 18, 10 12, 12 12 C 14 12, 16 6, 19 6" />
          <circle cx="5" cy="18" r="1.8" fill={color} stroke="none" />
          <circle cx="19" cy="6" r="1.8" />
          <path d="M19 6 v-2.5" />
        </svg>
      );

    case "bookmarks":
      // Two stacked bookmarks: front one larger and pinned, back
      // one offset for depth. Reads as "saved items, plural."
      return (
        <svg {...baseProps}>
          <path d="M9 4 h7 a1.5 1.5 0 0 1 1.5 1.5 V19 l-5-3.5 -5 3.5 V5.5 a1.5 1.5 0 0 1 1.5 -1.5 z" />
          <path d="M11 4 V3 a1.5 1.5 0 0 1 1.5 -1.5 h5 a1.5 1.5 0 0 1 1.5 1.5 V16" />
        </svg>
      );

    case "leaderboard":
      // Three podium bars: 2nd, 1st, 3rd. The center bar is tallest,
      // with a small star/crown notch at the top edge.
      return (
        <svg {...baseProps}>
          <rect x="3" y="12.5" width="5" height="8.5" rx="0.75" />
          <rect x="9.5" y="6.5" width="5" height="14.5" rx="0.75" />
          <rect x="16" y="14.5" width="5" height="6.5" rx="0.75" />
          <path d="M11 4.5 l1 -2 l1 2" stroke={color} strokeWidth="1.5" />
        </svg>
      );

    case "3cq":
      // Triangle of 3 nodes connected. The three points are the
      // 3CQ dimensions: Critical Thinking (top), Communication
      // (bottom-left), Collaboration (bottom-right). Filled dots at
      // each vertex give the triangle visual weight.
      return (
        <svg {...baseProps}>
          <path d="M12 5 L 5.5 18 L 18.5 18 Z" />
          <circle cx="12" cy="5" r="1.6" fill={color} stroke="none" />
          <circle cx="5.5" cy="18" r="1.6" fill={color} stroke="none" />
          <circle cx="18.5" cy="18" r="1.6" fill={color} stroke="none" />
        </svg>
      );

    case "profile":
      // Person silhouette: refined head + shoulders. Slightly larger
      // head circle than the platform default for better readability
      // at 18px.
      return (
        <svg {...baseProps}>
          <circle cx="12" cy="8.5" r="3.5" />
          <path d="M4.5 20 C 4.5 16, 7.5 14, 12 14 C 16.5 14, 19.5 16, 19.5 20" />
        </svg>
      );

    default:
      return null;
  }
};

export default SideNavIcon;
